import type { QueryClient } from "@tanstack/react-query";
import { knowledgeBaseService, type KnowledgeBaseItem } from "@/services/knowledgeBaseService";
import { extractTextFromBlob } from "@/utils/textExtractionHelper";
import {
    invalidateKnowledgeBaseListQueries,
    linkDocumentsToTopicAfterCreate,
} from "@/lib/linkDocumentToTopicIds";

export function isSupportedLocalKbFile(file: File): boolean {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();
    return (
        mimeType === "application/pdf" ||
        mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        mimeType === "application/msword" ||
        mimeType.startsWith("text/") ||
        mimeType === "application/json" ||
        mimeType === "text/csv" ||
        mimeType.startsWith("image/") ||
        fileName.endsWith(".pdf") ||
        fileName.endsWith(".docx") ||
        fileName.endsWith(".doc") ||
        fileName.endsWith(".txt") ||
        fileName.endsWith(".md") ||
        fileName.endsWith(".markdown") ||
        fileName.endsWith(".json") ||
        fileName.endsWith(".csv") ||
        /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(fileName)
    );
}

export function filterSupportedLocalFiles(files: File[]): File[] {
    return files.filter(isSupportedLocalKbFile);
}

async function createSpaceNoteFromLocalFile(
    file: File,
    spaceId: string | null,
    workspaceId: string | null
): Promise<KnowledgeBaseItem> {
    const filename = file.name;
    const blob = new Blob([file], { type: file.type });
    const extracted = await extractTextFromBlob(blob, filename, file.type || "application/octet-stream");

    let content = extracted.text?.trim() ?? "";
    if (extracted.error || !content) {
        const mime = file.type.toLowerCase();
        if (mime.startsWith("image/")) {
            content = `[Imported image: ${filename}]\nNo extractable text; open or replace content in the editor if needed.`;
        } else {
            throw new Error(extracted.error || "No text could be extracted from this file");
        }
    }

    return await knowledgeBaseService.createKnowledgeBaseItem(
        {
            title: filename.replace(/\.[^/.]+$/, ""),
            content,
            type: "note",
            spaceId,
            topicId: null,
            tagIds: [],
            folderId: null,
        },
        workspaceId ?? null
    );
}

export type DriveFileWithText = { text?: string; filename?: string };

export interface ImportToTopicContext {
    spaceId: string | null;
    topicId: string;
    workspaceId: string | null;
    queryClient: QueryClient;
    onRefresh?: () => void;
}

/**
 * Process local files: create in space (no topic), link all to topic, invalidate caches.
 */
export async function importLocalFilesToTopic(
    files: File[],
    ctx: ImportToTopicContext
): Promise<{ successCount: number; failCount: number }> {
    const valid = filterSupportedLocalFiles(files);
    let successCount = 0;
    let failCount = 0;
    const createdIds: string[] = [];

    for (const file of valid) {
        try {
            const item = await createSpaceNoteFromLocalFile(file, ctx.spaceId, ctx.workspaceId);
            if (item?.id) createdIds.push(item.id);
            successCount++;
            invalidateKnowledgeBaseListQueries(ctx.queryClient);
        } catch (e) {
            console.error(`Error processing file ${file.name}:`, e);
            failCount++;
        }
    }

    if (createdIds.length > 0) {
        await linkDocumentsToTopicAfterCreate(
            createdIds,
            ctx.topicId,
            ctx.spaceId,
            ctx.queryClient,
            ctx.onRefresh
        );
    }

    return { successCount, failCount };
}

/**
 * Drive-imported files with extracted text: create in space, link to topic.
 */
export async function importDriveFilesToTopic(
    files: DriveFileWithText[],
    ctx: ImportToTopicContext
): Promise<{ successCount: number; failCount: number }> {
    const valid = files.filter(
        (f) => f.text && typeof f.text === "string" && f.text.trim().length > 0
    );
    let successCount = 0;
    let failCount = 0;
    const createdIds: string[] = [];

    for (let i = 0; i < valid.length; i++) {
        const file = valid[i];
        const filename = file.filename ?? `Untitled Document ${i + 1}`;
        const fileContent = "text" in file && file.text ? file.text : "";
        try {
            const item = await knowledgeBaseService.createKnowledgeBaseItem(
                {
                    title: filename,
                    content: fileContent,
                    type: "note",
                    spaceId: ctx.spaceId,
                    topicId: null,
                    tagIds: [],
                    folderId: null,
                },
                ctx.workspaceId ?? null
            );
            if (item?.id) createdIds.push(item.id);
            successCount++;
        } catch (e) {
            console.error(`Error creating note for ${filename}:`, e);
            failCount++;
        }
    }

    if (createdIds.length > 0) {
        invalidateKnowledgeBaseListQueries(ctx.queryClient);
        await linkDocumentsToTopicAfterCreate(
            createdIds,
            ctx.topicId,
            ctx.spaceId,
            ctx.queryClient,
            ctx.onRefresh
        );
    }

    return { successCount, failCount };
}
